import { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, AreaChart, Area, Legend, CartesianGrid, ComposedChart } from "recharts";
import { format, parseISO, startOfDay, isBefore, eachWeekOfInterval, startOfWeek, endOfWeek } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

const STATUS_FILTERS = ["All", "Incomplete", "Complete", "Overdue"];
const STATUS_COLORS = {
  Complete: "hsl(var(--primary))",
  Incomplete: "hsl(var(--muted-foreground))"
};

const getTaskStatus = function(task) {
  if (task.status) return String(task.status).toLowerCase() === "complete" ? "complete" : "incomplete";
  if (typeof task.progress === "number") return task.progress >= 100 ? "complete" : "incomplete";
  return "incomplete";
};

const getTaskDueDate = function(task) {
  return task.dueDate || task.endDate;
};

const isTaskOverdue = function(task, today) {
  const status = getTaskStatus(task);
  if (status === "complete") return false;
  const due = getTaskDueDate(task);
  if (!due) return false;
  return isBefore(parseISO(due), today);
};

const filterTasksByStatus = function(tasks, filter, today) {
  if (filter === "All") return tasks;
  if (filter === "Complete") {
    return tasks.filter(function(t) { return getTaskStatus(t) === "complete"; });
  }
  if (filter === "Incomplete") {
    return tasks.filter(function(t) { return getTaskStatus(t) !== "complete"; });
  }
  if (filter === "Overdue") {
    return tasks.filter(function(t) { return isTaskOverdue(t, today); });
  }
  return tasks;
};

const getInitials = function(name) {
  if (!name) return "?";
  const parts = name.split(" ");
  let result = "";
  for (let i = 0; i < parts.length && i < 2; i++) {
    if (parts[i][0]) result += parts[i][0];
  }
  return result.toUpperCase();
};

const LollipopBar = function(props) {
  const { x, y, width, height, fill } = props;
  const centerX = x + width / 2;
  const lineY = y + height;
  return (
    <g>
      <line x1={centerX} y1={lineY} x2={centerX} y2={y} stroke={fill} strokeWidth="2" />
      <circle cx={centerX} cy={y} r="6" fill={fill} />
    </g>
  );
};

const AssigneeTick = function(props) {
  const { x, y, payload, resourcesMap } = props;
  const assignee = resourcesMap[payload.value];
  const name = assignee ? assignee.name : payload.value;
  const avatar = assignee ? assignee.avatar : "";
  return (
    <foreignObject x={x - 18} y={y + 6} width={36} height={36}>
      <div className="flex items-center justify-center">
        <Avatar className="h-8 w-8">
          {avatar && <AvatarImage src={avatar} alt={name} />}
          <AvatarFallback className="text-[10px]">{getInitials(name)}</AvatarFallback>
        </Avatar>
      </div>
    </foreignObject>
  );
};

export const Dashboard = ({ projects, tasks, resources, startDate, endDate }) => {
  const today = startOfDay(new Date());
  const [sectionFilter, setSectionFilter] = useState("Incomplete");
  const [statusFilter, setStatusFilter] = useState("All");
  const [assigneeFilter, setAssigneeFilter] = useState("Incomplete");
  const statusFilterOptions = STATUS_FILTERS;

  const projectMap = useMemo(function() {
    const map = {};
    for (let i = 0; i < projects.length; i++) {
      map[projects[i].id] = projects[i];
    }
    return map;
  }, [projects]);

  const resourcesMap = useMemo(function() {
    const map = { Unassigned: { id: "Unassigned", name: "Unassigned", avatar: "" } };
    for (let i = 0; i < resources.length; i++) {
      map[resources[i].id] = resources[i];
    }
    return map;
  }, [resources]);

  const kpis = useMemo(function() {
    let completed = 0;
    let incomplete = 0;
    let overdue = 0;
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];
      const status = getTaskStatus(task);
      if (status === "complete") {
        completed += 1;
      } else {
        incomplete += 1;
        if (isTaskOverdue(task, today)) {
          overdue += 1;
        }
      }
    }
    return {
      completed: completed,
      incomplete: incomplete,
      overdue: overdue,
      total: tasks.length
    };
  }, [tasks, today]);

  const sectionChartData = useMemo(function() {
    const filtered = filterTasksByStatus(tasks, sectionFilter, today);
    const counts = {};
    for (let i = 0; i < filtered.length; i++) {
      const task = filtered[i];
      const section = task.section || (projectMap[task.projectId] ? projectMap[task.projectId].name : "Unassigned");
      counts[section] = (counts[section] || 0) + 1;
    }
    return Object.keys(counts).map(function(section) {
      return { section: section, count: counts[section] };
    });
  }, [tasks, sectionFilter, today, projectMap]);

  const donutBase = useMemo(function() {
    const filtered = filterTasksByStatus(tasks, statusFilter, today);
    let complete = 0;
    let incomplete = 0;
    for (let i = 0; i < filtered.length; i++) {
      if (getTaskStatus(filtered[i]) === "complete") {
        complete += 1;
      } else {
        incomplete += 1;
      }
    }
    return {
      total: filtered.length,
      data: [
        { name: "Complete", value: complete },
        { name: "Incomplete", value: incomplete }
      ]
    };
  }, [tasks, statusFilter, today]);
  const donutData = donutBase.data;

  const assigneeChartData = useMemo(function() {
    const filtered = filterTasksByStatus(tasks, assigneeFilter, today);
    const counts = {};
    for (let i = 0; i < filtered.length; i++) {
      const task = filtered[i];
      if (task.resourceAllocations && task.resourceAllocations.length > 0) {
        for (let a = 0; a < task.resourceAllocations.length; a++) {
          const rid = task.resourceAllocations[a].resourceId;
          counts[rid] = (counts[rid] || 0) + 1;
        }
      } else {
        counts.Unassigned = (counts.Unassigned || 0) + 1;
      }
    }
    return Object.keys(counts).map(function(assigneeId) {
      return { assigneeId: assigneeId, count: counts[assigneeId] };
    });
  }, [tasks, assigneeFilter, today]);

  const completionTrend = useMemo(function() {
    if (!startDate || !endDate) return [];
    const start = startOfWeek(parseISO(startDate), { weekStartsOn: 1 });
    const end = endOfWeek(parseISO(endDate), { weekStartsOn: 1 });
    const weeks = eachWeekOfInterval({ start: start, end: end }, { weekStartsOn: 1 });

    const result = [];
    for (let i = 0; i < weeks.length; i++) {
      const weekStart = weeks[i];
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      let complete = 0;
      let incomplete = 0;
      for (let t = 0; t < tasks.length; t++) {
        const task = tasks[t];
        const due = getTaskDueDate(task);
        if (!due) continue;
        const dueDate = parseISO(due);
        if (dueDate >= weekStart && dueDate <= weekEnd) {
          if (getTaskStatus(task) === "complete") {
            complete += 1;
          } else {
            incomplete += 1;
          }
        }
      }
      result.push({
        label: format(weekStart, "dd/MM"),
        complete: complete,
        incomplete: incomplete
      });
    }
    return result;
  }, [tasks, startDate, endDate]);

  const projectProgressData = useMemo(function() {
    const totals = {};
    const counts = {};
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];
      if (!task.projectId) continue;
      const progress = typeof task.progress === "number" ? task.progress : 0;
      totals[task.projectId] = (totals[task.projectId] || 0) + progress;
      counts[task.projectId] = (counts[task.projectId] || 0) + 1;
    }
    const result = [];
    for (let i = 0; i < projects.length; i++) {
      const project = projects[i];
      const count = counts[project.id] || 0;
      const avg = count > 0 ? Math.round(totals[project.id] / count) : 0;
      result.push({ projectId: project.id, name: project.name, progress: avg });
    }
    return result;
  }, [projects, tasks]);

  const projectProgressHeight = Math.max(240, projectProgressData.length * 48);

  return (
    <div className="flex flex-col gap-4">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-2xl font-bold">{kpis.completed}</div>
          <div className="text-xs text-muted-foreground">Completed tasks</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-2xl font-bold">{kpis.incomplete}</div>
          <div className="text-xs text-muted-foreground">Incomplete tasks</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-2xl font-bold">{kpis.overdue}</div>
          <div className="text-xs text-muted-foreground">Overdue tasks</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-2xl font-bold">{kpis.total}</div>
          <div className="text-xs text-muted-foreground">Total tasks</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Incomplete tasks by section</div>
            <select
              value={sectionFilter}
              onChange={function(e) { setSectionFilter(e.target.value); }}
              className="h-8 px-2 text-xs rounded-md border border-input bg-background"
            >
              {statusFilterOptions.map(function(f) {
                return <option key={f} value={f}>{f}</option>;
              })}
            </select>
          </div>
          <div className="flex-1 min-h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sectionChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="section" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Tasks by completion status</div>
            <select
              value={statusFilter}
              onChange={function(e) { setStatusFilter(e.target.value); }}
              className="h-8 px-2 text-xs rounded-md border border-input bg-background"
            >
              {statusFilterOptions.map(function(f) {
                return <option key={f} value={f}>{f}</option>;
              })}
            </select>
          </div>
          <div className="flex-1 min-h-[240px] flex items-center justify-between">
            <div className="w-2/3 h-full relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    stroke="hsl(var(--background))"
                  >
                    {donutData.map(function(entry) {
                      return <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />;
                    })}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-xs text-muted-foreground">Total</div>
                <div className="text-xl font-bold">{donutBase.total}</div>
              </div>
            </div>
            <div className="flex flex-col gap-2 text-xs">
              {donutData.map(function(item) {
                return (
                  <div key={item.name} className="flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[item.name] }} />
                    <span>{item.name}</span>
                    <span className="text-muted-foreground">{item.value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Tasks by assignee</div>
            <select
              value={assigneeFilter}
              onChange={function(e) { setAssigneeFilter(e.target.value); }}
              className="h-8 px-2 text-xs rounded-md border border-input bg-background"
            >
              {statusFilterOptions.map(function(f) {
                return <option key={f} value={f}>{f}</option>;
              })}
            </select>
          </div>
          <div className="flex-1 min-h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={assigneeChartData} margin={{ top: 8, right: 16, left: 0, bottom: 32 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="assigneeId"
                  tick={function(props) { return <AssigneeTick {...props} resourcesMap={resourcesMap} />; }}
                  interval={0}
                  height={50}
                />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" shape={LollipopBar} fill="hsl(var(--primary))" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Trend Row */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="text-sm font-medium mb-3">Project progress</div>
        <div style={{ height: projectProgressHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={projectProgressData} layout="vertical" margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
              <Tooltip formatter={function(value) { return [value + "%", "Progress"]; }} />
              <Bar dataKey="progress" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <div className="text-sm font-medium mb-3">Task completion over time</div>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={completionTrend}>
              <defs>
                <linearGradient id="completeFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="incompleteFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="complete"
                name="Complete"
                stroke="hsl(var(--primary))"
                fill="url(#completeFill)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="incomplete"
                name="Incomplete"
                stroke="hsl(var(--muted-foreground))"
                fill="url(#incompleteFill)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
